/**
 * V2 admin — Categories tab (V1 admin-categories.js parity).
 *
 * The catalog as an expandable tree with subtree location counts,
 * search that keeps matching branches intact (ancestors + descendants),
 * a create/edit form (emoji, parent, description, Collection + Can-Tag
 * flags), and delete with V1's safety moves: yoinks re-home to
 * Uncategorized, children step up to top level.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { invalidate } from '../../core/query.js';
import { categorySlug, searchVisibleIds } from '../../core/categories.js';
import { fetchAll } from './data.js';

/** Every descendant id of a category (not including itself). */
export function descendantIds(rootId, cats) {
  const ids = new Set();
  const walk = (id) => {
    cats.filter((c) => c.parent_id === id).forEach((child) => {
      if (!ids.has(child.id)) {
        ids.add(child.id);
        walk(child.id);
      }
    });
  };
  walk(rootId);
  return ids;
}

/**
 * Subtree unique-location counts per category, with NULL/orphaned
 * category ids counted under Uncategorized (V1 parity). Exported for tests.
 */
export function locationCounts(cats, yoinks) {
  const byId = new Map(cats.map((c) => [c.id, c]));
  const uncategorized = cats.find((c) => c.name.toLowerCase() === 'uncategorized');
  const direct = new Map();
  yoinks.forEach((y) => {
    if (!y.location_id) return;
    let id = y.category_id;
    if (!id || !byId.has(id)) id = uncategorized?.id;
    if (!id) return;
    if (!direct.has(id)) direct.set(id, new Set());
    direct.get(id).add(y.location_id);
  });
  const totals = {};
  cats.forEach((cat) => {
    const all = new Set(direct.get(cat.id) || []);
    descendantIds(cat.id, cats).forEach((descId) => {
      (direct.get(descId) || []).forEach((locId) => all.add(locId));
    });
    totals[cat.id] = all.size;
  });
  return totals;
}

// Branch-preserving search moved to the shared catalog module so the
// browse page uses the identical rule; re-exported for existing callers.
export { searchVisibleIds };

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let cats = [];
  let counts = {};
  let loaded = false;
  let search = '';
  let expanded = new Set();
  let editing = null; // null = closed, {} = new, {...cat} = edit
  let formError = '';
  let saving = false;
  // categories.article (20260813) is applied by hand; until it is, selecting
  // the column 400s the whole read. Remembered per mount: one failed try,
  // then every reload skips it and the editor hides itself.
  let articleSupported = true;

  async function load() {
    const catSelect = 'id,name,description,parent_id,emoji,is_set,can_tag';
    const loadCats = async () => {
      if (articleSupported) {
        try {
          return await fetchAll('categories', { select: `${catSelect},article`, order: 'name.asc' });
        } catch {
          articleSupported = false; // pre-migration database — fall through
        }
      }
      return fetchAll('categories', { select: catSelect, order: 'name.asc' });
    };
    const [categories, yoinks] = await Promise.all([
      loadCats(),
      fetchAll('yoinks', { select: 'category_id,location_id' }),
    ]);
    cats = categories;
    counts = locationCounts(cats, yoinks);
    loaded = true;
  }

  function rowHtml(cat, depth, visible) {
    const kids = cats
      .filter((c) => c.parent_id === cat.id)
      .filter((c) => !visible || visible.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    const isExpanded = expanded.has(cat.id) || (search && visible);
    const row = `
      <tr>
        <td class="admin-td-emoji">${cat.emoji || '📍'}</td>
        <td style="padding-left:${depth * 22 + 8}px">
          <span class="admin-cat-name">
            ${kids.length ? `
              <button type="button" class="admin-expand ${isExpanded ? 'is-open' : ''}"
                data-expand-cat="${cat.id}" aria-label="${isExpanded ? 'Collapse' : 'Expand'}">▶</button>`
              : '<span class="admin-expand-gap"></span>'}
            <span>${escapeHtml(cat.name)}</span>
            ${cat.is_set ? '<span class="admin-badge">COLLECTION</span>' : ''}
            ${cat.can_tag === false ? '<span class="admin-badge admin-badge-dim">NO TAG</span>' : ''}
          </span>
        </td>
        <td class="admin-num">${counts[cat.id] ?? 0}</td>
        <td>
          <div class="admin-actions">
            <button type="button" class="admin-btn admin-btn-xs" data-view-cat="${cat.id}">View</button>
            <button type="button" class="admin-btn admin-btn-xs" data-edit-cat="${cat.id}">Edit</button>
            <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
              data-delete-cat="${cat.id}" data-cat-name="${escapeHtml(cat.name)}">Delete</button>
          </div>
        </td>
      </tr>
      ${isExpanded ? kids.map((k) => rowHtml(k, depth + 1, visible)).join('') : ''}`;
    return row;
  }

  function parentOptionsHtml(exclude) {
    const options = (parentId, depth) => cats
      .filter((c) => (c.parent_id || null) === parentId && !exclude.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => `
        <option value="${c.id}" ${editing?.parent_id === c.id ? 'selected' : ''}>
          ${depth ? '─'.repeat(depth) + ' ' : ''}${escapeHtml(c.name)}</option>
        ${options(c.id, depth + 1)}`)
      .join('');
    return `<option value="">None (top level)</option>${options(null, 0)}`;
  }

  /**
   * One article section as form fields. Add/remove work directly on the DOM
   * (never render()) so an in-progress name or description edit is not wiped
   * by growing the article — save() reads whatever blocks exist at submit.
   */
  function articleSectionHtml(section = {}) {
    return `
      <div class="admin-article-section" data-article-section>
        <input type="text" data-article-heading placeholder="Section heading (optional)"
          value="${escapeHtml(section.heading || '')}">
        <textarea data-article-body rows="4"
          placeholder="Plain text — a blank line starts a new paragraph.">${escapeHtml(section.body || '')}</textarea>
        <button type="button" class="admin-btn admin-btn-xs" data-article-remove>Remove section</button>
      </div>`;
  }

  /**
   * The "About this collection" article editor (categories.article,
   * 20260813) — the landing-page text the category page and the crawler
   * block both render. Hidden entirely until the migration is applied.
   * Plain text by design: no markdown to type on an iPad, no HTML to escape
   * wrong — both renderers escape everything.
   */
  function articleEditorHtml() {
    if (!articleSupported) return '';
    const sections = Array.isArray(editing?.article) ? editing.article : [];
    return `
      <fieldset class="admin-article-editor" data-article-editor>
        <legend>About this ${editing?.is_set ? 'collection' : 'category'} (article)</legend>
        <small class="admin-muted">Shown at the foot of the page and served to search engines.
          Write it once, edit it as things change. Leave empty for no article.
          Links: paste a URL, or [label](https://…) — HTML tags show as text.</small>
        <div data-article-sections>${sections.map(articleSectionHtml).join('')}</div>
        <button type="button" class="admin-btn admin-btn-xs" data-article-add>+ Add section</button>
      </fieldset>`;
  }

  /** Collect the article from the dialog's blocks; null when empty. */
  function readArticle(form) {
    const blocks = [...form.querySelectorAll('[data-article-section]')];
    const sections = blocks.map((block) => ({
      heading: block.querySelector('[data-article-heading]')?.value.trim() || undefined,
      body: block.querySelector('[data-article-body]')?.value.trim() || '',
    })).filter((s) => s.body)
      .map((s) => (s.heading ? s : { body: s.body }));
    return sections.length ? sections : null;
  }

  function formHtml() {
    const isEdit = Boolean(editing?.id);
    const exclude = isEdit ? new Set([editing.id, ...descendantIds(editing.id, cats)]) : new Set();
    const hasChildren = isEdit && cats.some((c) => c.parent_id === editing.id);
    return `
      <dialog class="v2-auth-dialog admin-cat-dialog" data-cat-dialog>
        <h2 class="v2-dialog-title">${isEdit ? 'Edit category' : 'Add category'}</h2>
        <form data-cat-form>
          <label class="v2-field">Emoji
            <input type="text" name="emoji" class="admin-emoji-input" value="${escapeHtml(editing?.emoji || '📍')}">
          </label>
          <label class="v2-field">Name
            <input type="text" name="name" required value="${escapeHtml(editing?.name || '')}">
          </label>
          <label class="v2-field">Parent category
            <select name="parent">${parentOptionsHtml(exclude)}</select>
          </label>
          <label class="v2-field">Description
            <textarea name="description" rows="3">${escapeHtml(editing?.description || '')}</textarea>
          </label>
          ${articleEditorHtml()}
          ${hasChildren ? `
          <label class="admin-check">
            <input type="checkbox" name="isSet" ${editing?.is_set ? 'checked' : ''}>
            Mark as Collection (Set)
            <small>Collections track progress across every child — "all 63 National Parks".</small>
          </label>` : ''}
          <label class="admin-check">
            <input type="checkbox" name="canTag" ${editing?.can_tag !== false ? 'checked' : ''}>
            Can tag yoinks
            <small>Uncheck for organizational parents (e.g. "Cuisine") — their yoinks move to Uncategorized.</small>
          </label>
          ${formError ? `<p class="v2-form-error">${escapeHtml(formError)}</p>` : ''}
          <div class="admin-confirm-actions">
            <button type="button" class="admin-btn" data-cat-cancel>Cancel</button>
            <button type="submit" class="admin-btn admin-btn-primary" ${saving ? 'disabled' : ''}>
              ${saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </dialog>`;
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Sorting the card catalog…</div>';
      return;
    }
    const visible = searchVisibleIds(cats, search.trim());
    const roots = cats
      .filter((c) => !c.parent_id)
      .filter((c) => !visible || visible.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-cat-search aria-label="Search categories" placeholder="Search categories…"
            value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${cats.length} categories</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-expand-all>Expand all</button>
          <button type="button" class="admin-btn" data-collapse-all>Collapse all</button>
          <button type="button" class="admin-btn admin-btn-primary" data-add-cat>+ Add category</button>
        </div>
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead><tr><th></th><th>Name</th><th class="admin-num">Locations</th><th>Actions</th></tr></thead>
            <tbody>
              ${roots.length ? roots.map((c) => rowHtml(c, 0, visible)).join('')
                : '<tr><td colspan="4" class="admin-empty-cell">No categories found</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>
      ${editing !== null ? formHtml() : ''}`;

    const dialog = el.querySelector('[data-cat-dialog]');
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    if (dialog) setTimeout(() => dialog.querySelector('input[name="name"]')?.focus(), 50);

    const searchEl = el.querySelector('[data-cat-search]');
    searchEl?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      render();
      const again = el.querySelector('[data-cat-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
  }

  async function save(form) {
    const name = form.name.value.trim();
    const emoji = form.emoji.value.trim() || '📍';
    const parentId = form.parent.value || null;
    const description = form.description.value.trim();
    const isSet = form.isSet ? form.isSet.checked : Boolean(editing?.is_set);
    const canTag = form.canTag.checked;
    if (!name) { formError = 'Category name is required.'; return render(); }
    const duplicate = cats.find((c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== editing?.id);
    if (duplicate) { formError = 'A category with this name already exists.'; return render(); }

    // Read the article BEFORE render() rebuilds the dialog and drops the
    // section blocks the admin just typed into.
    const article = articleSupported ? readArticle(form) : undefined;

    saving = true;
    formError = '';
    render();
    try {
      const body = { name, emoji, description: description || null, parent_id: parentId, is_set: isSet, can_tag: canTag };
      if (articleSupported) {
        body.article = article;
        // Stamp only a real change — a rename must not claim the article
        // was freshened (the column is a freshness signal for the SEO layer)
        if (JSON.stringify(article) !== JSON.stringify(editing?.article ?? null)) {
          body.article_updated_at = new Date().toISOString();
        }
      }
      if (editing?.id) {
        // Turning tagging OFF re-homes existing yoinks (V1 parity)
        const wasTaggable = editing.can_tag !== false;
        if (wasTaggable && !canTag) {
          const uncategorized = cats.find((c) => c.name.toLowerCase() === 'uncategorized');
          if (!uncategorized) throw new Error('Create an "Uncategorized" category first.');
          await gateway.rawUpdate(gateway.TABLES.YOINKS, {
            filters: { category_id: `eq.${editing.id}` },
            body: { category_id: uncategorized.id },
          });
        }
        const { error } = await gateway.rawUpdate(gateway.TABLES.CATEGORIES, {
          filters: { id: `eq.${editing.id}` }, body,
        });
        if (error) throw error;
        showToast(`Saved ${isSet ? 'as a Collection' : ''}`.trim() + '.', { type: 'success' });
      } else {
        const { error } = await gateway.rawInsert(gateway.TABLES.CATEGORIES, { body });
        if (error) throw error;
        showToast('Category created.', { type: 'success' });
      }
      invalidate('categories:all'); // the app-wide catalog cache is stale now
      editing = null;
      await load();
    } catch (err) {
      formError = err.message || 'Couldn’t save the category.';
    }
    saving = false;
    if (!destroyed) render();
  }

  async function remove(catId, name) {
    const uncategorized = cats.find((c) => c.name.toLowerCase() === 'uncategorized');
    if (!uncategorized) {
      return showToast('Create an "Uncategorized" category first — deleted categories re-home their yoinks there.', { type: 'info' });
    }
    if (!(await confirmDialog({
      title: `Delete "${name}"?`,
      message: 'Its yoinks move to Uncategorized and its children become top-level. This can’t be undone.',
    }))) return;
    try {
      await gateway.rawUpdate(gateway.TABLES.YOINKS, {
        filters: { category_id: `eq.${catId}` },
        body: { category_id: uncategorized.id },
      });
      await gateway.rawUpdate(gateway.TABLES.CATEGORIES, {
        filters: { parent_id: `eq.${catId}` },
        body: { parent_id: null },
      });
      const { error } = await gateway.rawDelete(gateway.TABLES.CATEGORIES, {
        filters: { id: `eq.${catId}` },
      });
      if (error) throw error;
      invalidate('categories:all');
      showToast('Category deleted — yoinks moved to Uncategorized.', { type: 'success' });
      await load();
    } catch {
      showToast('Couldn’t delete it — it may have associated records.', { type: 'info' });
    }
    if (!destroyed) render();
  }

  function onClick(event) {
    const t = event.target;
    const expandBtn = t.closest('[data-expand-cat]');
    if (expandBtn) {
      const id = expandBtn.dataset.expandCat;
      if (expanded.has(id)) expanded.delete(id);
      else expanded.add(id);
      return render();
    }
    if (t.closest('[data-expand-all]')) {
      cats.forEach((c) => { if (cats.some((k) => k.parent_id === c.id)) expanded.add(c.id); });
      return render();
    }
    if (t.closest('[data-collapse-all]')) { expanded = new Set(); return render(); }
    if (t.closest('[data-add-cat]')) { editing = {}; formError = ''; return render(); }
    if (t.closest('[data-cat-cancel]')) {
      el.querySelector('[data-cat-dialog]')?.close?.();
      editing = null;
      return render();
    }
    // Article sections grow and shrink on the live DOM — a render() here
    // would wipe every unsaved field in the dialog (see articleSectionHtml)
    if (t.closest('[data-article-add]')) {
      const host = el.querySelector('[data-article-sections]');
      if (host) {
        host.insertAdjacentHTML('beforeend', articleSectionHtml());
        host.lastElementChild?.querySelector('[data-article-heading]')?.focus();
      }
      return;
    }
    const articleRemove = t.closest('[data-article-remove]');
    if (articleRemove) {
      articleRemove.closest('[data-article-section]')?.remove();
      return;
    }
    const viewBtn = t.closest('[data-view-cat]');
    if (viewBtn) {
      const cat = cats.find((c) => c.id === viewBtn.dataset.viewCat);
      if (cat) router.navigate('category', { slug: categorySlug(cat) });
      return;
    }
    const editBtn = t.closest('[data-edit-cat]');
    if (editBtn) {
      editing = cats.find((c) => c.id === editBtn.dataset.editCat) || null;
      formError = '';
      return render();
    }
    const deleteBtn = t.closest('[data-delete-cat]');
    if (deleteBtn) return remove(deleteBtn.dataset.deleteCat, deleteBtn.dataset.catName);
  }

  function onSubmit(event) {
    const form = event.target.closest('[data-cat-form]');
    if (form) {
      event.preventDefault();
      save(form);
    }
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the catalog.</div>'; });

  el.addEventListener('click', onClick);
  el.addEventListener('submit', onSubmit);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
    el.removeEventListener('submit', onSubmit);
  };
}
